// The floor evidence-photo pipeline (spec 04 §2.11 / §3.3 step 10, D-096):
// capture → durable blob queue → the REAL three-call upload on reconnect →
// the flag ships `photo_file_ids`.
//
// THE COUPLING, DECIDED AND RECORDED (D-096). §2.11's corrected column note
// already states the shape: "the floor outbox enqueues the upload first and
// derives these ids from its response (§3.4)". So the FLAG waits in the outbox
// until its blobs are `stored` — but the TAP is still one tap: flag and blobs
// are both durably queued at tap time, and it is only the DRAIN that runs the
// upload before the event. The alternative (send the flag photo-less and
// reconcile the ids later) was rejected because it would need a server
// attach-photos-to-handoff mutation that does not exist, and such an endpoint
// would let any holder attach evidence to any accusation after the fact —
// the exact after-the-fact editability §2.11 exists to kill.
//
// WHY A REPLAYED FLAG CAN NEITHER ORPHAN NOR DOUBLE-STORE ITS PHOTO. The
// three-call flow is resumable from the durable PhotoRecord: `fileId` is
// persisted before the PUT is attempted, the PUT and store() are idempotent
// per the files contract, and `storedFileId` is FINAL once set. The flag's
// payload is composed from `storedFileId`s at send time, under the flag's own
// tap-time `client_event_id` — so a replay after a dropped response carries
// byte-identical `photo_file_ids` and dedupes (D-080), and a crash at any
// point resumes with the same ids. The one non-ideal path is a presign that
// EXPIRES before its PUT lands: the registry cannot re-presign an existing
// pending row, so call 1 runs again with a fresh id and the old `pending` row
// is orphaned — inert (never `stored`, so never attachable, never resolvable),
// and recorded as the accepted cost in D-096.
//
// FAILURE STATES ARE THE TRAY'S, not new machinery: every refusal below throws
// through the outbox drain's existing classifier, so a photo that cannot
// upload retries with backoff and — after the §9.2 horizon or on a terminal
// refusal — surfaces the FLAG as needs-attention in the tray with the server's
// own sentence. Never silently dropped (the §9.1 conflict-tray precedent).

import type { components } from "@assessly/api-client";
import { client } from "../../../lib/api";
import { RequestError, unwrap } from "../../../lib/request";
import { uuidV7 } from "../../../lib/uuid";
// The pure-limits subpath (D-096): the same two values the server's purpose
// catalog is built from, WITHOUT dragging the OpenAPI registry into the
// floor bundle.
import { FILE_PHOTO_CONTENT_TYPES, FILE_PHOTO_MAX_BYTES } from "@assessly/contracts/files/limits";
import { currentFloorPrincipal } from "./floor-principal";
import type { FloorEvidencePurpose, PhotoRecord, PhotoStore } from "./photo-store";

type S = components["schemas"];
export type FileCreateResponse = S["FileCreateResponse"];
export type FileStoreResponse = S["FileStoreResponse"];

/** The §5.1 wire cap (`photo_file_ids: z.array(Uuid).max(10)`). */
export const MAX_FLAG_PHOTOS = 10;

// ---------------------------------------------------------------------------
// Capture-time validation — the SERVER's own limits, enforced BEFORE queueing
// ---------------------------------------------------------------------------

/**
 * Refuse at the camera what the server would refuse at the registry (D-096): a
 * queued blob the server will 422 is a guaranteed stuck tray entry hours later,
 * when the drum is long gone. The limits are the CONTRACT's own constants —
 * the same pair `file-purposes.ts` builds the server catalog from — so the two
 * refusals cannot drift apart.
 *
 * An EMPTY `File.type` (some Android camera intents) is refused as wrong-type
 * rather than guessed: call 1 signs the declared type into the presign, so a
 * guessed type that is wrong becomes an integrity rejection three calls later.
 */
export function floorPhotoProblem(file: {
  readonly type: string;
  readonly size: number;
}): "wrong_type" | "too_large" | undefined {
  const bare = (file.type.split(";")[0] ?? "").trim().toLowerCase();
  if (!(FILE_PHOTO_CONTENT_TYPES as readonly string[]).includes(bare)) return "wrong_type";
  if (file.size > FILE_PHOTO_MAX_BYTES) return "too_large";
  return undefined;
}

/** Lowercase-hex SHA-256 of a blob — what call 1 declares and call 3 verifies. */
export async function sha256HexOf(blob: Blob): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", await blob.arrayBuffer());
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

export interface CaptureDeps {
  readonly mint?: (now: number) => string;
  readonly hash?: (blob: Blob) => Promise<string>;
  readonly now?: () => number;
  readonly principal?: () => string | undefined;
  /** The registry purpose the blobs will be created under (D-064 pt 8).
   * Defaults to `production_evidence` — the defect flag's purpose, and what
   * every record queued before this option existed was uploaded as. S8's
   * waste capture MUST pass `waste_evidence`: module 05's ledger refuses any
   * other purpose (spec 05 §2.3), and the mismatch is unfixable post-queue. */
  readonly purpose?: FloorEvidencePurpose;
}

/**
 * Persist captured photos into the durable blob queue and return their ids, in
 * order. Validation runs FIRST and the whole batch is refused if any file
 * fails — a flag half of whose evidence queued is worse than one that asked
 * the operator to retake. Resolves only once every blob is durable, so a
 * caller that then enqueues the flag has the W-before-anything ordering the
 * outbox's kill-safety argument needs (blobs first, flag second: a death
 * between them leaves an orphaned blob, never a flag pointing at nothing).
 */
export async function capturePhotos(
  store: PhotoStore,
  files: readonly File[],
  deps: CaptureDeps = {},
): Promise<string[]> {
  const mint = deps.mint ?? uuidV7;
  const hash = deps.hash ?? sha256HexOf;
  const now = deps.now ?? (() => Date.now());
  const principal = deps.principal ?? currentFloorPrincipal;
  for (const file of files) {
    const problem = floorPhotoProblem(file);
    if (problem !== undefined) throw new Error(`photo refused: ${problem}`);
  }
  const ids: string[] = [];
  for (const file of files) {
    const record: PhotoRecord = {
      photoId: mint(now()),
      purpose: deps.purpose ?? "production_evidence",
      bytes: file,
      mime: (file.type.split(";")[0] ?? "").trim().toLowerCase(),
      byteSize: file.size,
      sha256: await hash(file),
      capturedAt: now(),
      principal: principal() ?? null,
      fileId: null,
      uploadUrl: null,
      uploadExpiresAtMs: null,
      uploaded: false,
      storedFileId: null,
      createKey: mint(now()),
      storeKey: mint(now()),
    };
    await store.put(record);
    ids.push(record.photoId);
  }
  return ids;
}

// ---------------------------------------------------------------------------
// The upload port the outbox drain calls (floor-outbox.ts `photos` option)
// ---------------------------------------------------------------------------

export interface PhotoUploadPort {
  /** Run/resume the three-call flow for every id; answer the STORED file ids in
   * the same order. Throws (RequestError / transport error) on the first
   * failure — the drain's classifier decides retry vs park exactly as it does
   * for the event send itself. */
  ensureUploaded(photoIds: readonly string[]): Promise<string[]>;
  /** Drop the device-side blobs (flag applied / conflicted / discarded /
   * cleared). Never throws — cleanup must not take the queue down. */
  discard(photoIds: readonly string[] | undefined): Promise<void>;
}

/** Injectable wire seam so the unit tests drive the state machine without a
 * network; the defaults are the REAL calls. Both API calls carry the record's
 * PERSISTED `Idempotency-Key` (see PhotoRecord.createKey/storeKey): a
 * transport-lost success replays to the same answer — same file id, no
 * duplicate row — and a key the server REFUSED under is rotated before the
 * next attempt, because the platform idempotency store replays stored error
 * responses too. */
export interface PhotoUploadWire {
  /** Call 1 — POST /v1/files, under the RECORD's own purpose (D-064 pt 8:
   * `production_evidence` for defect flags, `waste_evidence` for S8 waste
   * captures — a hardcoded production purpose here was the 2026-08-09 defect
   * that parked every waste capture with a photo). */
  createFile(
    input: {
      purpose: FloorEvidencePurpose;
      contentType: string;
      byteSize: number;
      sha256: string;
      fileName: string;
    },
    idempotencyKey: string,
  ): Promise<{ fileId: string; uploadUrl: string; uploadExpiresAtMs: number }>;
  /** Call 2 — the presigned PUT, bytes straight to the object store. */
  putBytes(uploadUrl: string, bytes: Blob, contentType: string): Promise<void>;
  /** Call 3 — POST /v1/files/{id}/store. Must throw RequestError on refusal. */
  storeFile(fileId: string, idempotencyKey: string): Promise<void>;
}

function synthetic(status: number, code: string, title: string): RequestError {
  return new RequestError({ type: "about:blank", status, title, code }, status, title);
}

/** Transport-shaped error (no HTTP status): the drain classifies it RETRY. */
function transport(message: string): Error {
  return new Error(message);
}

export function realPhotoUploadWire(): PhotoUploadWire {
  return {
    createFile: async (input, idempotencyKey) => {
      const res = unwrap(
        await client.POST("/v1/files", {
          params: { header: { "Idempotency-Key": idempotencyKey } },
          body: {
            purpose: input.purpose,
            content_type: input.contentType,
            byte_size: input.byteSize,
            sha256: input.sha256,
            file_name: input.fileName,
          },
        }),
        "file-create",
      );
      return {
        fileId: res.file.id,
        uploadUrl: res.upload_url,
        uploadExpiresAtMs: Date.parse(res.upload_expires_at),
      };
    },
    putBytes: async (uploadUrl, bytes, contentType) => {
      let res: Response;
      try {
        res = await fetch(uploadUrl, {
          method: "PUT",
          body: bytes,
          headers: { "Content-Type": contentType },
        });
      } catch (cause) {
        throw transport(`evidence PUT failed: ${String(cause)}`);
      }
      if (!res.ok) throw transport(`evidence PUT answered ${String(res.status)}`);
    },
    storeFile: async (fileId, idempotencyKey) => {
      unwrap(
        await client.POST("/v1/files/{id}/store", {
          params: { path: { id: fileId }, header: { "Idempotency-Key": idempotencyKey } },
        }),
        "file-store",
      );
    },
  };
}

export function createPhotoUploader(
  store: PhotoStore,
  wire: PhotoUploadWire = realPhotoUploadWire(),
  now: () => number = () => Date.now(),
  mint: (now: number) => string = uuidV7,
): PhotoUploadPort {
  const uploadOne = async (photoId: string): Promise<string> => {
    let record = await store.get(photoId);
    if (record === undefined) {
      // The blob is gone (a cleared store under a surviving flag record). No
      // retry can ever conjure the bytes back — terminal, so the tray gets it
      // now instead of after 24 h of retries.
      throw synthetic(
        422,
        "PHOTO_MISSING",
        "The queued evidence photo is no longer on this device",
      );
    }
    if (record.storedFileId !== null) return record.storedFileId;

    // Call 1 (or re-run after an expired presign — see the header on why an
    // expiry means a FRESH id rather than a refreshed URL).
    const expired =
      record.uploadExpiresAtMs !== null && !record.uploaded && now() >= record.uploadExpiresAtMs;
    if (record.fileId === null || record.uploadUrl === null || expired) {
      // An expired presign means a FRESH registry row, so the create key must
      // rotate WITH it (a reused key would replay the OLD response — the very
      // row whose URL just expired), and the rotation is persisted BEFORE the
      // call so a crash mid-create replays the same fresh key.
      if (expired || record.fileId !== null) {
        record = { ...record, createKey: mint(now()) };
        await store.put(record);
      }
      let created;
      try {
        created = await wire.createFile(
          {
            // Pre-field v2 records queued before `purpose` existed were all
            // defect-flag evidence — production_evidence is their truth.
            purpose: record.purpose ?? "production_evidence",
            contentType: record.mime,
            byteSize: record.byteSize,
            sha256: record.sha256,
            fileName: `evidence-${record.photoId}.${extensionOf(record.mime)}`,
          },
          record.createKey,
        );
      } catch (error) {
        // The server ANSWERED a refusal under this key; the idempotency store
        // will replay that refusal forever, so the key retires with it. A
        // transport failure keeps the key — replaying it is exactly how a
        // lost success resumes with the SAME file id.
        if (error instanceof RequestError) {
          await store.put({ ...record, createKey: mint(now()) });
        }
        throw error;
      }
      record = {
        ...record,
        fileId: created.fileId,
        uploadUrl: created.uploadUrl,
        uploadExpiresAtMs: created.uploadExpiresAtMs,
        uploaded: false,
      };
      // Durable BEFORE the PUT: the replay spine (see photo-store.ts header).
      await store.put(record);
    }

    // Call 2.
    if (!record.uploaded) {
      if (record.uploadUrl === null || record.fileId === null) {
        throw transport("photo record lost its presign mid-flight");
      }
      await wire.putBytes(record.uploadUrl, record.bytes, record.mime);
      record = { ...record, uploaded: true };
      await store.put(record);
    }

    // Call 3.
    if (record.fileId === null) throw transport("photo record lost its file id mid-flight");
    try {
      await wire.storeFile(record.fileId, record.storeKey);
    } catch (error) {
      if (error instanceof RequestError && error.code === "FILE_NOT_UPLOADED") {
        // The store did not see the object our PUT claimed to land (lost PUT /
        // object-store lag). Re-run the PUT on the next attempt; retryable via
        // the drain's code allowlist. The store key rotates with it — the 422
        // is now the stored answer under the old one.
        await store.put({ ...record, uploaded: false, storeKey: mint(now()) });
      } else if (
        error instanceof RequestError &&
        (error.code === "FILE_INTEGRITY_MISMATCH" || error.code === "FILE_REJECTED")
      ) {
        // The row is terminally rejected server-side (re-shoot = a NEW id, the
        // registry's own rule). Reset so a tray Retry starts a fresh call 1
        // with the same bytes; the refusal itself parks the flag loudly.
        await store.put({
          ...record,
          fileId: null,
          uploadUrl: null,
          uploadExpiresAtMs: null,
          uploaded: false,
          createKey: mint(now()),
          storeKey: mint(now()),
        });
      } else if (error instanceof RequestError) {
        // Any other ANSWERED refusal: retire the key it was stored under.
        await store.put({ ...record, storeKey: mint(now()) });
      }
      throw error;
    }
    const finalId = record.fileId;
    record = { ...record, storedFileId: finalId };
    await store.put(record);
    return finalId;
  };

  return {
    ensureUploaded: async (photoIds) => {
      const fileIds: string[] = [];
      for (const photoId of photoIds) fileIds.push(await uploadOne(photoId));
      return fileIds;
    },
    discard: async (photoIds) => {
      for (const photoId of photoIds ?? []) {
        try {
          await store.remove(photoId);
        } catch {
          // Cleanup must never take the queue down; an undeleted blob is
          // reclaimed on the next clear().
        }
      }
    },
  };
}

function extensionOf(mime: string): string {
  switch (mime) {
    case "image/jpeg":
      return "jpg";
    case "image/png":
      return "png";
    case "image/webp":
      return "webp";
    case "image/heic":
      return "heic";
    default:
      return "bin";
  }
}
